import type { Worksheet } from "exceljs";
import { MAX_APPEND_EMPTY_COLUMNS } from "../constants";
import { COLUMN_HEADER_FILL, THIN_BORDER } from "./cellStyle";

/**
 * The renderer trusts no upstream clamp: a count outside `0..MAX_APPEND_EMPTY_COLUMNS` (or a fraction)
 * means a settings bug somewhere, and silently clamping it would print a sheet the user never asked for.
 */
export const assertAppendEmptyColumns = (count: number): void => {
	if (!Number.isInteger(count) || count < 0 || count > MAX_APPEND_EMPTY_COLUMNS) {
		throw new Error(`appendEmptyColumns must be a whole number from 0 to ${MAX_APPEND_EMPTY_COLUMNS}, got ${count}`);
	}
};

interface BlankHeaderCellsArgs {
	worksheet: Worksheet;
	row: number;
	/** 1-based column of the first blank cell — the one right after the section's last real column. */
	firstColumn: number;
	count: number;
}

/**
 * Header-styled blank cells (bold, grey header fill, thin border) so the appended columns read as part
 * of the header band beside them. Used for the column-name row and, in a pivot, the band-label row too —
 * a blank column has no band, so it takes the same neutral header look in both rows rather than a band's
 * blue. No value is written: the column is meant to be filled in by hand.
 */
export const emitBlankHeaderCells = ({ worksheet, row, firstColumn, count }: BlankHeaderCellsArgs): void => {
	for (let offset = 0; offset < count; offset++) {
		const cell = worksheet.getCell(row, firstColumn + offset);
		cell.font = { bold: true };
		cell.fill = COLUMN_HEADER_FILL;
		cell.border = THIN_BORDER;
	}
};

interface BlankDataCellsArgs {
	worksheet: Worksheet;
	startRow: number;
	/** Inclusive. A section with no data rows passes `startRow - 1`, which emits nothing. */
	endRow: number;
	firstColumn: number;
	count: number;
}

/**
 * Bordered blank cells beside a section's data rows: the same thin border every data cell gets, with
 * the default font and alignment. Deliberately run AFTER the row renderer, so the row decorations
 * (pending / pending-delete fill, PTO strikethrough) — which stop at the last real column — never
 * reach them: the blank columns are for writing on, not part of the shift's status.
 */
export const emitBlankDataCells = ({ worksheet, startRow, endRow, firstColumn, count }: BlankDataCellsArgs): void => {
	for (let row = startRow; row <= endRow; row++) {
		for (let offset = 0; offset < count; offset++) {
			worksheet.getCell(row, firstColumn + offset).border = THIN_BORDER;
		}
	}
};
